(ns hypnos.consumer.panels.consult
  "Consult and the associated patient."
  (:require [hypnos.consumer.panels.consult.chronic-issues :as chronic-issues]
            [hypnos.consumer.panels.consult.claustrophobia-level :as claustrophobia-level]
            [hypnos.consumer.panels.consult.consent :as consent]
            [hypnos.consumer.panels.consult.dentures :as dentures]
            [hypnos.consumer.panels.consult.deviated-septum :as deviated-septum]
            [hypnos.consumer.panels.consult.done :as done]
            [hypnos.consumer.panels.consult.eye-dryness :as eye-dryness]
            [hypnos.consumer.panels.consult.issues :as issues]
            [hypnos.consumer.panels.consult.mouth-breathing-level :as mouth-breathing-level]
            [hypnos.consumer.panels.consult.not-found :as not-found]
            [hypnos.consumer.panels.consult.pacemaker :as pacemaker]
            [hypnos.consumer.panels.consult.portrait :as portrait]
            [hypnos.consumer.panels.consult.sleep-motion :as sleep-motion]
            [hypnos.consumer.panels.consult.sleep-positions :as sleep-positions]
            [hypnos.consumer.panels.consult.weight :as weight]
            [hypnos.graphql.queries :as queries]
            [hypnos.translator.consult :as t-c]
            [medley.core :as mc]
            [re-frame.core :as rf]
            [reagent.core :as reagent]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Identities

(def ^{:private true} defaults
  {;; remote communication
   :loading?   false
   ;; workflow
   :step       :consent
   :step-count 0
   ;; consult
   :consult    {:consult/id                    {:value    nil
                                                :dirty?   false
                                                :touched? false}
                ;; consent
                :consult/consented-at          {:value    nil
                                                :dirty?   false
                                                :touched? false}
                ;; personal info
                :consult/weight                {:value    nil
                                                :dirty?   false
                                                :touched? false
                                                :valid?   nil}
                ;; sleep info
                :consult/take-dentures-out?    {:value    nil
                                                :dirty?   false
                                                :touched? false}
                :consult/sleep-positions       {:value    #{}
                                                :dirty?   false
                                                :touched? false}
                :consult/sleep-motion?         {:value    nil
                                                :dirty?   false
                                                :touched? false}
                :consult/mouth-breathing-level {:value    nil
                                                :dirty?   false
                                                :touched? false}
                :consult/eye-dryness?          {:value    nil
                                                :dirty?   false
                                                :touched? false}
                :consult/claustrophobia-level  {:value    nil
                                                :dirty?   false
                                                :touched? false}
                ;; clinical information
                :consult/issues                {:value    #{}
                                                :dirty?   false
                                                :touched? false}
                :consult/chronic-issues?       {:value    nil
                                                :dirty?   false
                                                :touched? false}
                :consult/deviated-septum?      {:value    nil
                                                :dirty?   false
                                                :touched? false}
                :consult/pacemaker?            {:value    nil
                                                :dirty?   false
                                                :touched? false}
                ;; image
                :consult/image                 {:value    nil
                                                :dirty?   false
                                                :touched? false}}})

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- re-service-type?
  [consult]
  (contains? #{:service-type/refit :service-type/repap} (:consult/service-type consult)))

(defn- study-type?
  [consult]
  (contains? #{:service-type/sleep-study :service-type/home-study} (:consult/service-type consult)))

(defn- calc-step-max
  "Calculate the maximum number of steps based on the consult."
  [consult]
  (when consult
    (let [mask-uncomfortable? (not (:consult/mask-was-comfortable? consult))
          re-service-type?    (re-service-type? consult)
          study-type?         (study-type? consult)
          require-portrait?   (not (:consult/has-portrait? consult))]
      (cond-> 8
        require-portrait?                           (+ 1)
        (and mask-uncomfortable? (not study-type?)) (+ 1)
        re-service-type?                            (+ 2)))))

(defn- nav-fn
  [state current-step direction]
  (let [consult             (rf/subscribe [:consumer/consult])
        mask-uncomfortable? (not (:consult/mask-was-comfortable? @consult))
        re-service-type?    (re-service-type? @consult)
        study-type?         (study-type? @consult)
        require-portrait?   (not (:consult/has-portrait? @consult))
        step-count          (:step-count @state)]
    (condp = current-step
      :consent               (swap! state assoc
                                    :step :weight
                                    :step-count (inc step-count))
      ;; demographics
      :weight                (when (= direction :next)
                               (swap! state assoc
                                      :step :dentures
                                      :step-count (inc step-count)))
      ;; sleep
      :dentures              (if (= direction :next)
                               (swap! state assoc
                                      :step :sleep-positions
                                      :step-count (inc step-count))
                               (swap! state assoc
                                      :step :weight
                                      :step-count (dec step-count)))
      :sleep-positions       (if (= direction :next)
                               (swap! state assoc
                                      :step :sleep-motion
                                      :step-count (inc step-count))
                               (swap! state assoc
                                      :step :dentures
                                      :step-count (dec step-count)))
      :sleep-motion          (if (= direction :next)
                               (swap! state assoc
                                      :step :mouth-breathing-level
                                      :step-count (inc step-count))
                               (swap! state assoc
                                      :step :sleep-positions
                                      :step-count (dec step-count)))
      :mouth-breathing-level (if (= direction :next)
                               (if re-service-type?
                                 (swap! state assoc
                                        :step :eye-dryness
                                        :step-count (inc step-count))
                                 (swap! state assoc
                                        :step :chronic-issues
                                        :step-count (inc step-count)))
                               (swap! state assoc
                                      :step :sleep-positions
                                      :step-count (dec step-count)))
      :eye-dryness           (if (= direction :next)
                               (swap! state assoc
                                      :step :claustrophobia-level
                                      :step-count (inc step-count))
                               (swap! state assoc
                                      :step :mouth-breathing-level
                                      :step-count (dec step-count)))
      :claustrophobia-level  (if (= direction :next)
                               (swap! state assoc
                                      :step :chronic-issues
                                      :step-count (inc step-count))
                               (swap! state assoc
                                      :step :eye-dryness
                                      :step-count (dec step-count)))
      ;; clinical issues
      :chronic-issues        (if (= direction :next)
                               (if (and mask-uncomfortable?
                                        (not study-type?))
                                 (swap! state assoc
                                        :step :issues
                                        :step-count (inc step-count))
                                 (swap! state assoc
                                        :step :deviated-septum
                                        :step-count (inc step-count)))
                               (if re-service-type?
                                 (swap! state assoc
                                        :step :claustrophobia-level
                                        :step-count (dec step-count))
                                 (swap! state assoc
                                        :step :mouth-breathing-level
                                        :step-count (dec step-count))))
      :issues                (if (= direction :next)
                               (swap! state assoc
                                      :step :deviated-septum
                                      :step-count (inc step-count))
                               (swap! state assoc
                                      :step :chronic-issues
                                      :step-count (dec step-count)))
      :deviated-septum       (if (= direction :next)
                               (swap! state assoc
                                      :step :pacemaker
                                      :step-count (inc step-count))
                               (if (and mask-uncomfortable?
                                        (not study-type?))
                                 (swap! state assoc
                                        :step :issues
                                        :step-count (dec step-count))
                                 (swap! state assoc
                                        :step :chronic-issues
                                        :step-count (dec step-count))))
      :pacemaker             (if (= direction :next)
                               (if require-portrait?
                                 (swap! state assoc
                                        :step :portrait
                                        :step-count (inc step-count))
                                 (swap! state assoc
                                        :step :done
                                        :step-count (inc step-count)))
                               (swap! state assoc
                                      :step :deviated-septum
                                      :step-count (dec step-count)))
      :portrait              (if (= direction :next)
                               (swap! state assoc
                                      :step :done
                                      :step-count (inc step-count))
                               (swap! state assoc
                                      :step :pacemaker
                                      :step-count (dec step-count)))
      :done                  (swap! state assoc :step :done)
      (swap! state assoc :step nil))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Panel

(defn panel
  "The patient consult panel"
  [{:keys [_consult-key]}]
  (let [state (reagent/atom defaults)]
    (fn [{:keys [consult-key]}]
      (when (and consult-key
                 (not (:loading? @state)))
        (rf/dispatch-sync [:amplify/query-api
                           queries/get-consult-by-key
                           {:key consult-key}
                           :consumer/set-consult
                           :consumer/noop])
        (swap! state assoc :loading? true))
      (let [consult    (rf/subscribe [:consumer/consult])
            _          (when @consult
                         (swap! state
                                mc/deep-merge
                                (t-c/consult->state @consult)))
            step-max   (calc-step-max @consult)
            step-count (:step-count @state)]
        [:div.card
         [:div.card-header
          [:p.card-header-title
           [:progress.progress.is-info {:max step-max :value step-count} step-count]]]
         (condp = (:step @state)
           :consent               [consent/step state nav-fn]
           :weight                [weight/step state nav-fn]
           :dentures              [dentures/step state nav-fn]
           :sleep-positions       [sleep-positions/step state nav-fn]
           :sleep-motion          [sleep-motion/step state nav-fn]
           :mouth-breathing-level [mouth-breathing-level/step state nav-fn]
           :eye-dryness           [eye-dryness/step state nav-fn]
           :claustrophobia-level  [claustrophobia-level/step state nav-fn]
           :chronic-issues        [chronic-issues/step state nav-fn]
           :issues                [issues/step state nav-fn]
           :deviated-septum       [deviated-septum/step state nav-fn]
           :pacemaker             [pacemaker/step state nav-fn]
           :portrait              [portrait/step state nav-fn]
           :done                  [done/step]
           [not-found/step])]))))
